import { createSlice } from '@reduxjs/toolkit'
import { groupBy } from 'lodash-es'
import { Trans } from 'react-i18next'
import { Alerts, Donkey, FMT, MS, newDate, SupportChat, useObjSelector } from 'utils'
import HTTP_CODE from 'utils/HTTP_CODE'
import Restaurant from './models/Restaurant'
import { fetchOrders } from './Orders'
import API from './API'
import { getAccount } from './Account'
import { setDefaultTimezone } from './Constants'

export const ALL_LOCATIONS_ACTIVE = 0
const NO_LOCATION_ACTIVE = null

const slice = createSlice({
	name        : 'restaurants',
	initialState: {
		list      : [],
		lastActive: NO_LOCATION_ACTIVE,
	},
	reducers    : {
		internalSetter     : (state, { payload: list }) => { state.list = list },
		clearRestaurants   : state => { state.list = [] },
		setActiveRestaurant: (state, { payload: activeId }) => {
			if (activeId == NO_LOCATION_ACTIVE || activeId == ALL_LOCATIONS_ACTIVE) {
				state.lastActive = activeId
				setDefaultTimezone(state.list[0].timezone_id)
				console.log('All [0] / no [null] locations are active;', activeId)
			}

			state.list.forEach(/** @type {Restaurant} */ restaurant => {
				if (restaurant.active = (restaurant.id == activeId)) {
					//will never arrive here if activeId is either NO or ALL
					state.lastActive = restaurant.id
					setDefaultTimezone(restaurant.timezone_id)
					console.log(`Active restaurant: #${restaurant.id} ${restaurant.display_name} [${FMT.dateFlex('HH:mm zzz', newDate(null))}]`)
				}
			})
		},
		updateRestaurant    : ({ list }, { payload }) => {
			const restaurant = list.find(r => r.id == payload.id)
			if (restaurant) {
				Object.assign(restaurant, payload)
			}
		},

		setRestaurantShipdayAutoDispatchEnabled: ({ list }, { payload: { id, enabled }}) => {
			list.find(r => r.id == id).shipday_auto_dispatch_enabled = enabled
		}
	},
})

/**
 * @see SupportChat.event
 * @param event {string} The event name
 * @param ids {int[]|int|string} Either restaurant IDs (to select from the available list) or a fixed string (sent as `{ value: "something" }`)
 * @param getState
 */
function sendRestaurantEvent(event, ids, getState) {
	const state       = getState()
	const supportChat = SupportChat.fromState(state)

	if (supportChat?.supports(SupportChat.FEAT_EVENTS)) {
		let payload
		if (typeof ids == 'string' || typeof ids == 'number') {
			payload = { value: ids }
		} else {
			payload = getRestaurants(state)
				.filter(r => ids.includes(parseInt(r.id)))
				.map(r => [`#${r.id}`, r.display_name])

			payload = !payload.length?
				{ IDs: ids.join(', ') } :
				Object.fromEntries(payload)
		}

		supportChat.event(event, payload)
	}
}

//not exactly official, but enough for some organization
slice.thunks = {
	refreshRestaurants: async dispatch => {
		try {
			const resp = await API.getAndHandle('restaurant?activeOnly=false', HTTP_CODE.NOT_MODIFIED__304)
			dispatch(slice.thunks.setRestaurants(resp.data, true))
		} catch(e) {
			console.debug('No changes to restaurants')
		}
	},

	reopenRestaurants: ids => async (dispatch, getState) => {
		sendRestaurantEvent('Reopened restaurant(s)', ids, getState)
		await API.patch('restaurant/open', { ids })
		await dispatch(slice.thunks.refreshRestaurants)
	},

	/**
	 * @param restaurant {Restaurant}
	 * @param periods {Object<string, int>} Minute periods, indexed by order type
	 * @returns {(function(*, *): Promise<void>)|*}
	 */
	toggleRestaurant: (restaurant, periods) => async (dispatch, getState) => {
		sendRestaurantEvent('Toggled restaurant', restaurant.id, getState)
		const result = (await API.patch(`restaurant/${restaurant.id}/toggle`, { periods })).data
		dispatch(slice.actions.updateRestaurant(result))
	},

//not "real" thunks, but actually a grouping of dispatch calls
	/**
	 * @param list {array}
	 * @param skipAlert {boolean}
	 * @return {(function(*, *): Promise<void>)|*}
	 */
	setRestaurants: (list, skipAlert = false) => async (dispatch, getState) => {
		let account = getAccount(getState())

		Restaurant.defineColors(list, account.id) //the ID is used as a fixed randomizer seed

		await dispatch(slice.actions.internalSetter(list))
		const state = getState()
		const rests = filterEnabled(state.restaurants.list)
		const lastActive = state.restaurants.lastActive

		let activate
		if (rests.length == 1) {
			activate = rests[0].id
		} else {
			activate = state.restaurants.lastActive
			if (
				lastActive != NO_LOCATION_ACTIVE &&
				lastActive != ALL_LOCATIONS_ACTIVE &&
				!rests.some(r => r.id == lastActive) //still a valid location - not deleted, nor from past-logged accounts
			) {
				activate = NO_LOCATION_ACTIVE
			}
		}
		dispatch(slice.actions.setActiveRestaurant(activate))

		if (!skipAlert && activate == NO_LOCATION_ACTIVE && rests.length > 1) {
			const LOCATIONS = account.location_wording_plural
			Alerts.info(<Trans>You are viewing orders for <strong>All {{ LOCATIONS }}</strong>. You can select a specific one in the bottom of the left menu.</Trans>, { perm: true })
		}
	},

	setActiveRestaurant: id => async (dispatch, getState) => {
		id = parseInt(id)
		sendRestaurantEvent('Changed restaurant', id? [id] : 'all restaurants', getState)
		dispatch(slice.actions.setActiveRestaurant(id))
		dispatch(fetchOrders)
	},

	sendGMBPIN: (id, pin) => async () => {
		await API.post(`restaurant/${id}/gmb/pin`, { pin })
	},

	toggleAutoDispatch: (id, enabled) => async dispatch => {
		const finalState = (await API.patch(`restaurant/${id}/auto_dispatch`, { enabled })).data
		dispatch(slice.actions.setRestaurantShipdayAutoDispatchEnabled({ id, enabled: finalState }))
	},

	/** Refreshes restaurants every 2 minutes to sync with changes from the Admin. */
	setupRestaurantRefreshes: async (dispatch, getState) => {
		Donkey.askAbout(Donkey.TOPIC.THE_RESTAURANTS, 2*MS.MIN, getState, () => {
			dispatch(slice.thunks.refreshRestaurants) //originally, this did two refresh calls - restaurants and account -, but the latter was removed in favor of the already existing X-Suggested-Refresh header for accounts. Such header was also added for restaurants, but since push-enabled, low-volume places could take a while to receive the custom header, this timer is still in place until pushes are implemented everywhere and for all updatable topics
		})
	}
}

// /**
//  * @param id
//  * @return {function(state: { restaurants: { list: array }}): Restaurant|null}
//  * @to-do AVOID copying this over to other places! Try to find a generic way to do this...maybe adding a hint inside Restaurant telling where its list is found inside the state, and then doing like Restaurant.byId(id), but coming from the Model class instead
//  */
// export const getRestaurantById = id => ({ restaurants }) => {
// 	const data = restaurants.list.find(r => r.id == id)
// 	return data? new Restaurant(data) : null
// }

/** @return Restaurant|null */
const filterActive  = list => list.find(r => r.active)
/** @return Restaurant[] */
const filterEnabled = list => list.filter(r => !r.is_locked)

/** Use only if you can't really use {@link useRestaurants}
 * @return Model[]|Restaurant[] */
export const getRestaurants = ({ restaurants: { list } }) => Restaurant.map(filterEnabled(list))
/** Only returns enabled (unlocked) restaurants.
 * @see getEvenLockedRestaurants
 * @return Restaurant[] */
export const useRestaurants = () => useObjSelector(getRestaurants)

/** Returns even the locked ("deactivated") restaurants. Ugly naming so you're really sure you know what you're doing.
 * @return Model[]|Restaurant[] */
export const getEvenLockedRestaurants = ({ restaurants: { list } }) => Restaurant.map(list)

/**
 * Might return an empty Restaurant object if the restaurant is not present on the list - e.g. got deleted.
 * @param id
 * @returns {function({restaurants: {list: *}}): Restaurant}
 */
export const getRestaurant = id => ({ restaurants: { list } }) => {
	let data = list.find(r => r.id == id)
	if (!data) {
		console.error('Restaurant not found in local data!', id)
	}
	return new Restaurant(data || {})
}

/**
 * @param id
 * @return {function(object): (function(object): Restaurant)|null}
 */
export const getRestaurantIfMany = id => state => {
	const { restaurants: { list } } = state
	return list.length > 1? getRestaurant(id)(state) : null
}

export const getIsSingleRestaurant = ({ restaurants: { list }}) => (list.length == 1)
export const getIsSingleEnabledRestaurant = ({ restaurants: { list }}) => (filterEnabled(list).length == 1)

/**
 * Use only with the direct state at hand. Otherwise, go with {@link useActiveRestaurant()}, which is reload-safe.
 * @param state {{ restaurants: Object[] }}
 * @return {Restaurant|null}
 * @internal
 */
export const getActiveRestaurant = ({ restaurants: { list } }) => {
	const restaurant = filterActive(list)
	return restaurant? new Restaurant(restaurant) : null
}

/**
 * Returns restaurants grouped by their statuses as active, open, partially or totally closed - and also a total counter.
 * @return {{ active: ?Restaurant, total: number, open: Restaurant[], closed: Restaurant[], allClosed: Restaurant[] }} The difference between `closed` and `allClosed` is that the first includes restaurants only partially closed.
 */
export const useRestaurantsByStatus = () => useObjSelector(state => {
	/** @type Restaurant[] */
	const restaurants = getRestaurants(state)
	const active      = filterActive(restaurants)
	const allClosed   = restaurants.filter(r => r.isPublicOrderingClosedForAllTypes())

	const {
		true : closed = [],
    false: open   = []
	} = groupBy(restaurants, r => r.isPublicOrderingClosedForAnyType())

	return { active, open, closed, allClosed, total: restaurants.length }
})

/**
 * Selects the active restaurant from the state, with a hook.
 * @returns {Restaurant|null} Yields null in case "All locations" is selected
 */
export const useActiveRestaurant = () => useObjSelector(getActiveRestaurant)

export const { clearRestaurants } = slice.actions

export const { refreshRestaurants, reopenRestaurants, toggleRestaurant, setRestaurants, setActiveRestaurant, sendGMBPIN, toggleAutoDispatch, setupRestaurantRefreshes } = slice.thunks
export default slice.reducer
